import React, { useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import logoImage from '../assets/Frame 1000005895.png';
import upgradeButtonImage from '../assets/Frame 9.png';
import projectsButtonImage from '../assets/Frame 5.png';
import languageButtonImage from '../assets/Frame 1000005885 (1).png';
import profileButtonImage from '../assets/Frame 2 (1).png';
import LanguageModal from '../components/LanguageModal';
import ProfileMenu from '../components/ProfileMenu';
import LoginModal from '../components/LoginModal.js';

const TermsOfService = () => {
  const navigate = useNavigate();
  const [isLanguageModalOpen, setIsLanguageModalOpen] = useState(false);
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [currentLanguage, setCurrentLanguage] = useState({ name: 'English', code: 'en-IN', flag: '🇮🇳' });
  const [currentUser, setCurrentUser] = useState(null);
  
  // Get the user data when component mounts
  React.useEffect(() => {
    const getUserFromStorage = () => {
      const storedUser = localStorage.getItem('currentUser');
      if (storedUser) {
        try {
          const parsedUser = JSON.parse(storedUser);
          setCurrentUser(parsedUser);
          return parsedUser;
        } catch (error) {
          console.error('Error parsing stored user:', error);
          return null;
        }
      }
      return null;
    };
    
    getUserFromStorage();
  }, []);
  
  const goToProjects = () => {
    navigate('/projects');
  };
  
  const goToUpgrade = () => {
    navigate('/upgrade');
  };

  const openLanguageModal = () => {
    setIsLanguageModalOpen(true);
  };

  const closeLanguageModal = () => {
    setIsLanguageModalOpen(false);
  };
  
  const openLoginModal = () => {
    setIsLoginModalOpen(true);
  };
  
  const closeLoginModal = () => {
    setIsLoginModalOpen(false);
  };
  
  const toggleProfileMenu = () => {
    setIsProfileMenuOpen(!isProfileMenuOpen);
  };
  
  const closeProfileMenu = () => {
    setIsProfileMenuOpen(false);
  };
  
  const handleLanguageChange = (language) => {
    setCurrentLanguage(language);
    console.log(`Language changed to: ${language.name} (${language.code})`);
  };
  
  // Handle profile button click based on login status
  const handleProfileButtonClick = () => {
    if (currentUser) {
      toggleProfileMenu();
    } else {
      openLoginModal();
    }
  };

  const navigateToSearch = () => {
    navigate('/');
  };

  return (
    <PageContainer>
      <Header>
        <LogoSection>
          <Logo src={logoImage} alt="Total AI" onClick={navigateToSearch}/>
        </LogoSection>
        <NavSection>
          <UpgradeButton onClick={goToUpgrade}>
            <UpgradeButtonImg src={upgradeButtonImage} alt="Upgrade" />
          </UpgradeButton>
          <ProjectsButton onClick={goToProjects}>
            <ProjectsButtonImg src={projectsButtonImage} alt="My Projects" />
          </ProjectsButton>
         
          <ProfileButton onClick={handleProfileButtonClick}>
            <ProfileButtonImg src={profileButtonImage} alt="Profile" />
          </ProfileButton>
        </NavSection>
      </Header>

      <ContentContainer>
        <ContentHeader>
          <SectionTitle>Terms and Conditions</SectionTitle>
          <LastUpdated>Last Updated: April, 2025</LastUpdated>
        </ContentHeader>

        <TermsContent>
          <Section>
            <SectionHeading>1. ACCEPTANCE OF TERMS</SectionHeading>
            <Paragraph>
              BattleXo Lab ("XO Lab," "we," "us," or "our") provides access to its website located at https://lab.battlexo.com/ (the "Website") and various services, resources, and content offered through the Website (collectively, the "Services"). Your access to and use of the Services are subject to and governed by the terms and conditions outlined in this Terms and Conditions agreement (this "Terms"). XO Lab may, at its discretion, update these Terms at any time, and depending on your location, we may require your explicit consent to any updates. You can review the most current version of these Terms on this page or by clicking on a clearly labeled "Terms and Conditions" or similar link within the Website.
            </Paragraph>
            <Paragraph>
              PLEASE READ THESE TERMS CAREFULLY. BY ACCESSING OR USING THE WEBSITE OR SERVICES, YOU AGREE TO BE BOUND BY THESE TERMS, INCLUDING ANY UPDATES OR REVISIONS POSTED ON THE WEBSITE. IF YOU DO NOT AGREE WITH THE TERMS AND CONDITIONS HEREIN, YOU MUST NOT ACCESS OR USE THE WEBSITE OR SERVICES.
            </Paragraph>
            <Paragraph>
              These Terms may include provisions for dispute resolution, such as arbitration, depending on XO Lab's specific legal and operational requirements. If such provisions are included in the full Terms and Conditions, they will be clearly stated.
            </Paragraph>
            <Paragraph>
              By accessing and using the Services, you represent and warrant that you: (i) are of legal age to form a binding contract in your jurisdiction; (ii) have the right, authority, and capacity to agree to and abide by these Terms; and (iii) are not prohibited from using the Services under the laws of your applicable jurisdiction. THE SERVICES ARE NOT INTENDED FOR USERS UNDER THE AGE OF 13 OR THE MINIMUM AGE REQUIRED BY APPLICABLE LAW FOR CONSENT TO ONLINE SERVICES, AND SUCH USERS ARE EXPRESSLY PROHIBITED FROM SUBMITTING ANY PERSONAL DATA OR USING ANY ASPECT OF THE SERVICES. BY USING THE SERVICES, YOU AGREE, REPRESENT, AND WARRANT THAT YOU MEET THIS AGE REQUIREMENT.
            </Paragraph>
          </Section>
          
          <Section>
            <SectionHeading>2. Account Registration</SectionHeading>
            <Paragraph>
              To access certain features of our platform, you may need to register for an account. When you register, you agree to provide accurate information and keep it updated. You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account.
            </Paragraph>
          </Section>
          
          <Section>
            <SectionHeading>3. Use of Services</SectionHeading>
            <Paragraph>
              Our services are designed to provide AI-powered gaming experiences and tools. You agree to use our services only for lawful purposes and in accordance with these Terms. You may not:
            </Paragraph>
            <BulletList>
              <ListItem>Use our services in any way that violates applicable laws or regulations</ListItem>
              <ListItem>Attempt to gain unauthorized access to any part of our services</ListItem>
              <ListItem>Interfere with or disrupt the integrity or performance of our services</ListItem>
              <ListItem>Reproduce, duplicate, copy, sell, or resell any portion of our services</ListItem>
              <ListItem>Use our services to develop competing products or services</ListItem>
            </BulletList>
          </Section>
          
          <Section>
            <SectionHeading>4. Intellectual Property</SectionHeading>
            <Paragraph>
              All content, features, and functionality of our platform, including but not limited to text, graphics, logos, icons, images, audio clips, digital downloads, and software, are owned by BattleXO AI or its licensors and are protected by copyright, trademark, and other intellectual property laws.
            </Paragraph>
            <Paragraph>
              We grant you a limited, non-exclusive, non-transferable, and revocable license to use our platform for personal, non-commercial purposes in accordance with these Terms.
            </Paragraph>
          </Section>
          
          <Section>
            <SectionHeading>5. User Content</SectionHeading>
            <Paragraph>
              You may have the opportunity to submit, post, or share content through our platform. By submitting content, you grant us a worldwide, non-exclusive, royalty-free license to use, reproduce, modify, adapt, publish, translate, and distribute such content in connection with our services.
            </Paragraph>
            <Paragraph>
              You represent and warrant that you own or have the necessary rights to the content you submit and that your content does not infringe upon the rights of any third party.
            </Paragraph>
          </Section>
          
          <Section>
            <SectionHeading>6. Privacy</SectionHeading>
            <Paragraph>
              Your privacy is important to us. Our <PrivacyLink onClick={() => navigate('/privacy')}>Privacy Policy</PrivacyLink> describes how we collect, use, and disclose information about you when you use our services. By using our platform, you consent to our collection and use of information as described in our Privacy Policy.
            </Paragraph>
          </Section>
          
          <Section>
            <SectionHeading>7. Subscription and Payments</SectionHeading>
            <Paragraph>
              We offer both free and paid subscription plans. By subscribing to a paid plan, you agree to pay all fees in accordance with the pricing and terms in effect at the time of your subscription. We may change our fees at any time, but will provide notice of any fee changes in advance.
            </Paragraph>
          </Section>
          
          <Section>
            <SectionHeading>8. Termination</SectionHeading>
            <Paragraph>
              We reserve the right to suspend or terminate your access to our services at any time, for any reason, without notice. Upon termination, your right to use our services will cease immediately, but all provisions of these Terms that by their nature should survive termination shall survive.
            </Paragraph>
          </Section>
          
          <Section>
            <SectionHeading>9. Disclaimer of Warranties</SectionHeading>
            <Paragraph>
              OUR SERVICES ARE PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT ANY WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED. WE DISCLAIM ALL WARRANTIES, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
            </Paragraph>
          </Section>
          
          <Section>
            <SectionHeading>10. Limitation of Liability</SectionHeading>
            <Paragraph>
              IN NO EVENT SHALL BATTLEXO AI BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING BUT NOT LIMITED TO LOSS OF PROFITS, DATA, OR USE, ARISING OUT OF OR IN CONNECTION WITH THESE TERMS OR YOUR USE OF OUR SERVICES.
            </Paragraph>
          </Section>
          
          <Section>
            <SectionHeading>11. Changes to Terms</SectionHeading>
            <Paragraph>
              We may modify these Terms at any time by posting the revised terms on our platform. Your continued use of our services after the effective date of any modifications constitutes your acceptance of the modified terms.
            </Paragraph>
          </Section>
          
          <Section>
            <SectionHeading>12. Contact Us</SectionHeading>
            <Paragraph>
              If you have any questions about these Terms, please contact us at support@battlexo.ai.
            </Paragraph>
          </Section>
        </TermsContent>
      </ContentContainer>

      <LanguageModal 
        isOpen={isLanguageModalOpen} 
        onClose={closeLanguageModal} 
        onLanguageChange={handleLanguageChange}
      />
      
      <ProfileMenu
        isOpen={isProfileMenuOpen}
        onClose={closeProfileMenu}
      />
      
      <LoginModal
        isOpen={isLoginModalOpen}
        onClose={closeLoginModal}
      />
    </PageContainer>
  );
};

// Styled Components
const PageContainer = styled.div`
  font-family: 'Space Grotesk', sans-serif;
  max-width: 100%;
  min-height: 100vh;
  background-color: #ffffff;
`;

const Header = styled.header`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
  border-bottom: 1px solid #eaeaea;
`;

const LogoSection = styled.div`
  display: flex;
  align-items: center;
`;

const Logo = styled.img`
  height: 24px;
  cursor: pointer;
`;

const NavSection = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const UpgradeButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  padding: 0;
  cursor: pointer;
`;

const UpgradeButtonImg = styled.img`
  height: 36px;
  width: auto;
`;

const ProjectsButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  padding: 0;
  cursor: pointer;
`;

const ProjectsButtonImg = styled.img`
  height: 36px;
  width: auto;
`;

const LanguageButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  padding: 0;
  cursor: pointer;
`;

const ButtonImage = styled.img`
  height: 36px;
  width: auto;
`;

const ProfileButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  padding: 0;
  cursor: pointer;
`;

const ProfileButtonImg = styled.img`
  height: 36px;
  width: auto;
`;

const ContentContainer = styled.main`
  max-width: 900px;
  margin: 0 auto;
  padding: 40px 24px;
`;

const ContentHeader = styled.div`
  margin-bottom: 32px;
  padding-bottom: 16px;
  border-bottom: 1px solid #eaeaea;
`;

const SectionTitle = styled.h1`
  font-size: 32px;
  font-weight: 700;
  background: linear-gradient(90deg, #E80694, #0782FE);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  margin-bottom: 8px;
`;

const LastUpdated = styled.p`
  font-size: 14px;
  color: #666;
`;

const TermsContent = styled.div`
  color: #333;
  line-height: 1.6;
`;

const Section = styled.section`
  margin-bottom: 32px;
`;

const SectionHeading = styled.h2`
  font-size: 24px;
  font-weight: 600;
  margin-bottom: 16px;
  color: #23232D;
`;

const Paragraph = styled.p`
  margin-bottom: 16px;
  font-size: 16px;
`;

const BulletList = styled.ul`
  margin-bottom: 16px;
  padding-left: 40px;
`;

const ListItem = styled.li`
  margin-bottom: 8px;
`;

const PrivacyLink = styled.span`
  color: #0782FE;
  cursor: pointer;
  text-decoration: underline;
  
  &:hover {
    color: #E80694;
  }
`;

export default TermsOfService; 